/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xAppState } from "@monsma/App/Store/Reducers";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import Comments from "@monsma/XDL/Icons/Comments";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    supportLabel = "supportLabel",
    supportNumber = "supportNumber",
    workingDaysLabel = "workingDaysLabel",
}

interface ContactSupportProps extends WidgetProps {
    fields: {
        [fields.supportLabel]: string;
        [fields.supportNumber]: string;
        [fields.workingDaysLabel]: string;
    };
}

const mapStateToProps = (state: xAppState) => ({
    pageType: getCurrentPage(state).type, // xng-change
});

type Props = ContactSupportProps & ReturnType<typeof mapStateToProps>;

export interface ContactSupportStyles {
    wrapper?: InjectableCss;
    icon?: IconProps;
    contentText?: InjectableCss;
    support?: InjectableCss;
    supportLabel?: InjectableCss;
    supportNumber?: LinkPresentationProps;
    workingDays?: InjectableCss;
}

export const contactSupportStyles: ContactSupportStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    icon: {
        src: Comments,
        size: 34,
        color: "primary.main",
        css: css`
            &.outer-icon {
                margin-right: 15px;

                ${screen("lte", "xs")} {
                    display: none;
                }
            }

            &.inner-icon {
                display: none;
                margin-right: 6px;

                ${screen("lte", "xs")} {
                    display: block;

                    &, svg {
                        height: 20px;
                    }
                }
            }
        `,
    },
    contentText: {
        css: css`
            &&& {
                text-align: left;
            }
        `,
    },
    support: {
        css: css`
            display: flex;
        `,
    },
    supportLabel: {
        css: css`
            margin-right: 5px;
            font-size: 15px;
            line-height: 20px;

            ${screen("lte", "xs")} {
                display: none;
            }
        `,
    },
    supportNumber: {
        typographyProps: {
            lineHeight: "20px",
            size: "15px",
        },
    },
    workingDays: {
        css: css`
            line-height: 16px;

            > * {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

const styles = contactSupportStyles;

const ContactSupport: React.FC<Props> = ({ pageType, fields }) => {
    if (!isItCheckoutPage(pageType)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <IconMemo {...styles.icon} className="outer-icon" />
            <StyledWrapper {...styles.contentText}>
                <StyledWrapper {...styles.support}>
                    <IconMemo {...styles.icon} width={18} className="inner-icon" />
                    <StyledWrapper {...styles.supportLabel}>{fields.supportLabel}:</StyledWrapper>
                    <Link {...styles.supportNumber} href={`tel:${fields.supportNumber}`}>
                        {fields.supportNumber}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.workingDays}>{parse(fields.workingDaysLabel, parserOptions)}</StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ContactSupport),
    definition: {
        group: "Header",
        displayName: "Contact Support",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.supportLabel,
                displayName: "Support Label",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.supportNumber,
                displayName: "Support Number",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.workingDaysLabel,
                displayName: "Working Days label",
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
